/*
 * Every colour, space, radius, font and shadow here is a design token.
 * The only literals are the rail's two widths, the logo slot that follows from them, and
 * the glider's geometry, which are structural rather than scale values.
 */

.base,
.base * {
  box-sizing: border-box;
}

/*
 * Width and how it expands belong to the component; where it sits belongs to the host.
 * Nothing here positions it — give it position: fixed through className. The delay is
 * on the way in only, so sweeping the pointer across doesn't flap it open, while leaving
 * closes it at once.
 */
.base {
  display: flex;
  flex-direction: column;

  width: 60px;
  height: 100%;
  overflow: hidden;

  background: var(--bg-surface);
  border-right: 1px solid var(--border-default);

  transition: width var(--duration-base) var(--ease-default) 0s;
}

/* Both the hover intent and the settle are timed in the component — see Sidebar.tsx.
   CSS can't express "hovered long enough to have finished widening", which is what a
   flyout has to wait for. */
.base[data-expanded] {
  width: 240px;
}

/* ── Head ── */

.head {
  display: flex;
  align-items: center;
  gap: var(--space-2);

  height: 72px;
  padding: 0 var(--space-4);
  flex-shrink: 0;
}

/* The logo is the home link, and owns a slot rather than a size: a fixed height, the width
   following whatever was passed. Apps used to size their own and no two agreed. */
.logo {
  display: flex;
  align-items: center;
  /* Grows, never shrinks below its logo. Shrinking, it let the pin in over the full logo
     while the rail was still widening; this way the pin is pushed past the rail's edge
     until there's room for it. */
  flex: 1 0 auto;
  align-self: stretch;

  border-radius: var(--radius-sm);
  color: inherit;
  text-decoration: none;
}

.logo:focus-visible {
  outline: var(--focus-ring);
  outline-offset: var(--focus-ring-offset);
}

/* A 32px square — the collapsed rail is 60px with --space-4 either side. The mark is
   fitted inside it: contain for an image, the viewBox's default "meet" for an SVG. */
.mark {
  display: block;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
}

.mark > :is(img, svg) {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* The full logo's height is fixed and its width follows its own proportions, capped at
   the room the fully expanded head leaves beside the pin — so one too wide shrinks rather
   than pushing the pin out.

   Capped against that final room, not the rail's current width: measured against the
   rail mid-widen, it showed up squashed to 28px and grew with the rail. At its final size
   from the start, the rail's overflow reveals it as it widens, as it does the labels. */
.full {
  display: none;
  flex-shrink: 0;
}

.full > :is(img, svg) {
  display: block;
  height: 32px;
  width: auto;
  max-width: calc(240px - var(--space-4) * 2 - var(--space-2) - var(--space-6));
  object-fit: contain;
}

/* Swapped on data-expanded, as the labels are — the full logo arrives as the rail starts
   to widen, and the rail's overflow reveals it as it goes. */
.base[data-expanded] .full {
  display: block;
}

.base[data-expanded] .markCollapsedOnly {
  display: none;
}

/* The pin is meaningless on a rail with no room for it, and it would sit under the
   logomark. It appears with the labels. */
/*
 * Held back until the rail has finished widening, then faded in — appearing mid-slide
 * reads as a glitch, and there is nowhere for it to sit on a 60px rail anyway.
 * visibility rather than opacity alone, so it isn't clickable or tabbable while hidden.
 */
.pin {
  flex-shrink: 0;

  width: var(--space-6);
  height: var(--space-6);
  padding: var(--space-1);

  border: 0;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--text-muted);
  cursor: pointer;

  opacity: 0;
  visibility: hidden;
  transition:
    opacity var(--duration-fast) var(--ease-default),
    visibility 0s linear var(--duration-fast),
    background-color var(--duration-fast) var(--ease-default),
    color var(--duration-fast) var(--ease-default);
  transition-delay: 0s, var(--duration-fast), 0s, 0s;
}

/* data-settled arrives only once the rail has stopped, so the fade needs no delay of its
   own. Visibility flips at once on the way in; on the way out it waits out the fade. */
.base[data-settled] .pin {
  opacity: 1;
  visibility: visible;
  transition-delay: 0s, 0s, 0s, 0s;
}

.pin:hover {
  background: var(--bg-hover);
  color: var(--text-primary);
}

.pin[aria-pressed='true'] {
  color: var(--accent-secondary);
}

.pin:focus-visible {
  outline: var(--focus-ring);
  outline-offset: var(--focus-ring-offset);
}

/* ── Nav ── */

.nav {
  position: relative; /* the glider is placed against this */
  flex: 1;

  display: flex;
  flex-direction: column;

  overflow-y: auto;
  overflow-x: hidden;
}

/* ── Rows ── */

.link {
  display: flex;
  align-items: center;

  height: 44px;
  padding: 0;
  flex-shrink: 0;

  border: 0;
  background: none;
  cursor: pointer;

  font-family: var(--font-sans);
  font-size: var(--text-md);
  font-weight: var(--weight-regular);
  line-height: var(--leading-normal);
  color: var(--text-secondary);
  text-decoration: none;
  white-space: nowrap;

  transition:
    background-color var(--duration-fast) var(--ease-default),
    color var(--duration-fast) var(--ease-default);
}

.link:hover {
  background: var(--bg-hover);
  color: var(--text-primary);
}

.link:focus-visible {
  outline: var(--focus-ring);
  outline-offset: calc(var(--focus-ring-offset) * -1);
}

/*
 * Current is read from the attribute the router already sets, so there is no active prop
 * to keep in sync. :has() lets a group header light up for a child in its flyout — it
 * matches on structure, so it holds while the panel is shut.
 */
.link[aria-current='page'],
.group:has([aria-current='page']) > .link {
  color: var(--accent-secondary);
  font-weight: var(--weight-semi);
}

.link[aria-current='page'] .icon,
.group:has([aria-current='page']) > .link .icon {
  color: var(--accent-secondary);
}

/*
 * As wide as the collapsed rail, so the icon is centred on it and stays exactly where it
 * is when the rail widens — expanding only reveals the label beside it. The reference
 * animated justify-content and padding instead, which moves every icon while the width
 * is already in flight.
 */
.icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  width: 60px;
  height: var(--space-5);
  color: var(--text-muted);
}

/* Sized in em against the label, as in Badge and Tabs — an unsized SVG is 300x150. */
.icon > svg {
  width: 1.25em;
  height: 1.25em;
}

/*
 * Labels are laid out at all times and revealed by opacity, not display. Toggling display
 * would re-flow every row mid-transition and the width animation would stutter against it.
 */
.label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: var(--space-3);

  opacity: 0;
  transition: opacity var(--duration-fast) var(--ease-default);
}

.trailing {
  flex-shrink: 0;
  margin-right: var(--space-4);
  opacity: 0;
  transition: opacity var(--duration-fast) var(--ease-default);
}

.chevron {
  flex-shrink: 0;
  margin-right: var(--space-4);
  width: var(--space-4);
  height: var(--space-4);
  color: var(--text-muted);
  opacity: 0;
  transition: opacity var(--duration-fast) var(--ease-default);
}

.base[data-expanded] .label,
.base[data-expanded] .trailing,
.base[data-expanded] .chevron {
  opacity: 1;
}

/* The flyout is its own surface and always expanded, so its labels never hide. */
.flyout .label,
.flyout .trailing {
  opacity: 1;
}

.divider {
  height: 0;
  margin: var(--space-2) var(--space-4);
  border: 0;
  border-top: 1px solid var(--border-subtle);
  flex-shrink: 0;
}

/* ── Glider ── */

/*
 * A 2px bar that slides to whatever is current, with a bloom either side and a wash of
 * colour into the row. Placed from --glider-y/-h, which the component measures.
 */
.glider {
  /* Overshoots and settles, so the bar arrives with a bit of weight rather than easing
     flatly into place. Local rather than a token until something else wants the same
     curve — @parrot/tokens is published, so adding to it is a conversation. */
  --ease-glider: cubic-bezier(0.37, 1.95, 0.66, 0.56);

  position: absolute;
  left: 0;
  top: 0;
  width: 2px;
  height: var(--glider-h, 44px);

  opacity: var(--glider-shown, 0);
  translate: 0 var(--glider-y, 0);

  pointer-events: none;
  background: linear-gradient(0deg, transparent 0%, var(--accent-secondary) 50%, transparent 100%);

  transition:
    translate var(--duration-slow) var(--ease-glider),
    height var(--duration-base) var(--ease-default),
    opacity var(--duration-fast) var(--ease-default);
}

/* Bloom — spreads sideways from the bar. */
.glider::before {
  content: '';
  position: absolute;
  top: 50%;
  height: 55%;
  width: 300%;
  translate: 0 -50%;
  background: var(--accent-secondary);
  filter: blur(9px);
  opacity: 0.9;
}

/* Wash — fades colour across the row behind the label. */
.glider::after {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 80px;
  background: linear-gradient(
    90deg,
    color-mix(in srgb, var(--accent-secondary) 13%, transparent) 0%,
    transparent 100%
  );
}

/* ── Flyout ── */

.flyout {
  inset: auto;
  margin: 0;
  padding: var(--space-1);

  width: 280px;
  max-height: 100vh;
  overflow-y: auto;

  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--bg-elevated);
  box-shadow: var(--shadow-lg);

  font-family: var(--font-sans);

  opacity: 0;
  translate: calc(var(--space-1) * -1) 0;
  transition:
    opacity var(--duration-fast) var(--ease-default),
    translate var(--duration-fast) var(--ease-default),
    overlay var(--duration-fast) allow-discrete,
    display var(--duration-fast) allow-discrete;
}

.flyout:popover-open {
  opacity: 1;
  translate: 0 0;
}

/* The "before" frame — a freshly shown element has no previous style to animate from. */
/*noinspection CssUnknownProperty*/
@starting-style {
  .flyout:popover-open {
    opacity: 0;
    translate: calc(var(--space-1) * -1) 0;
  }
}

.flyout .link {
  border-radius: var(--radius-sm);
}

/* The panel is its own surface, not the rail, so the icon column shrinks to the icon. */
.flyout .icon {
  width: var(--space-5);
  margin: 0 var(--space-2) 0 var(--space-3);
}

@media (prefers-reduced-motion: reduce) {
  .base,
  .pin,
  .link,
  .label,
  .trailing,
  .chevron,
  .glider,
  .glider::before {
    transition: none;
  }
  .flyout {
    transition-property: overlay, display;
  }
}
